export const sections = [
    {
        id: 'datatypes',
        title: 'Java Data Types',
        icon: '💎',
        bg: '#06b6d4',
        tagline: 'Primitives vs Objects',
        simple: 'Java is a statically-typed language, meaning every variable must have a declared type. Types are divided into Primitives (simple values like int, boolean) and Reference types (complex objects).',
        keyPoints: [
            '8 Primitives: byte, short, int, long, float, double, boolean, char.',
            'Precision: Use BigDecimal for financial calculations (avoids float/double errors).',
            'Wrapper Classes: Objects (Integer, Double) that wrap primitives for use in Collections.',
            'Autoboxing: The automatic conversion between primitives and their wrapper classes.'
        ],
        code: `// Java: Precision & Wrappers
double price = 0.1 + 0.2; // 0.30000000000000004 ❌
BigDecimal precise = new BigDecimal("0.1").add(new BigDecimal("0.2")); // 0.3 ✅

Integer wrapped = 10; // Autoboxing
int primitive = wrapped; // Unboxing

# Python Equivalent:
# Python's 'float' is like Java's 'double' (binary issues)
price = 0.1 + 0.2 # 0.30000000000000004
# Use 'Decimal' for precision
from decimal import Decimal
precise = Decimal('0.1') + Decimal('0.2') # 0.3`,
        qa: [
            {
                q: "Why should we avoid float and double for money?",
                a: "**Float and Double** use binary floating-point math which cannot exactly represent decimals like 0.1, leading to tiny rounding errors. **BigDecimal** provides exact precision for financial applications."
            },
            {
                q: "What is the memory difference between int and Integer?",
                a: "**int** is a primitive stored on the stack (4 bytes). **Integer** is an object stored on the heap with overhead (memory for object metadata), typically ~16-24 bytes."
            }
        ]
    },
    {
        id: 'oops',
        title: 'OOPs Principles',
        icon: '⚽',
        bg: '#f97316',
        tagline: 'The Foundation of Java',
        simple: 'Object-Oriented Programming (OOP) is a paradigm based on the concept of "objects". Java uses four main pillars to make code reusable, scalable, and secure.',
        keyPoints: [
            'Encapsulation: Bundling data and methods, hiding internals via "private".',
            'Inheritance: Subclasses "is-a" Parent class, promoting code reuse.',
            'Polymorphism: "Many forms" via Overloading (static) and Overriding (dynamic).',
            'Abstraction: Hiding complexity via interfaces and abstract classes.'
        ],
        code: `// Java: OOPs Principles
// 1. ABSTRACTION: Interface defines "What" (contract)
interface Account {
    void withdraw(double amount);
}

// 2. INHERITANCE: SavingsAccount "is-a" Account
class SavingsAccount implements Account {
    // 3. ENCAPSULATION: hidden data, controlled access
    private double balance;

    public void withdraw(double amount) {
        if (amount <= balance) balance -= amount;
    }

    // 4. POLYMORPHISM (Overloading): Same name, different params
    public void deposit(double amount) { balance += amount; }
    public void deposit(BigDecimal amount) { 
        balance += amount.doubleValue(); 
    }
}

// POLYMORPHISM (Overriding): BusinessAccount has its own rules
class BusinessAccount extends SavingsAccount {
    @Override
    public void withdraw(double amount) {
        // Business rules: allow overdraft
        super.withdraw(amount);
    }
}

# Python Equivalent:
from abc import ABC, abstractmethod

# 1. ABSTRACTION: Abstract Base Class defines "What"
class Account(ABC):
    @abstractmethod
    def withdraw(self, amount):
        pass

# 2. INHERITANCE: SavingsAccount "is-a" Account
class SavingsAccount(Account):
    # 3. ENCAPSULATION: hidden data (by convention), controlled access
    def __init__(self, balance=0.0):
        self._balance = balance # Protected by convention

    def withdraw(self, amount): # Overriding abstract method
        if amount <= self._balance:
            self._balance -= amount

    # 4. POLYMORPHISM (Overloading - simulated with default args)
    def deposit(self, amount, currency="USD"):
        print(f"Depositing {amount} {currency}")
        self._balance += amount

# POLYMORPHISM (Overriding): BusinessAccount has its own rules
class BusinessAccount(SavingsAccount):
    def withdraw(self, amount):
        # Business rules: allow overdraft
        print("Business account withdrawal logic")
        super().withdraw(amount) # Call parent method`,
        qa: [
            {
                q: "How does Abstraction differ from Encapsulation?",
                a: "**Abstraction** focuses on 'what' an object does (interface), while **Encapsulation** focuses on 'how' it does it and protecting that state (access modifiers)."
            },
            {
                q: "Can we override a static method in Java?",
                a: "No. Static methods belong to the class, not the instance. If you define the same method in a subclass, it is called **Method Hiding**, not polymorphism."
            }
        ]
    },
    {
        id: 'collections',
        title: 'Collections & Generics',
        icon: '📚',
        bg: '#3b82f6',
        tagline: 'Efficient Data Management',
        simple: 'The Collections Framework provides a unified architecture for representing and manipulating groups of objects.',
        keyPoints: [
            'Array: Fixed-size, contiguous memory. Fast random access (O(1)) but slow resizing/insertion.',
            'ArrayList: Dynamic array. Best for frequent reads (O(1)).',
            'LinkedList: Chain of nodes. Fast insertion/deletion (O(1) at head/tail).',
            'FIFO (Queue): First-In-First-Out. Use case: Task scheduling.',
            'LIFO (Stack): Last-In-First-Out. Use case: Undo/Redo, Call Stack.',
            'HashMap: Key-Value pairs with O(1) average lookup.',
            'Binary Search: Find elements in O(log N) - must be sorted!'
        ],
        code: `// Java: Data Structures Deep Dive
// 1. LINKED LIST (Manual implementation excerpt)
class Node { int data; Node next; }

// 2. STACK (LIFO)
Deque<Integer> stack = new ArrayDeque<>();
stack.push(1); // Add to top
int top = stack.pop(); // Remove from top

// 3. QUEUE (FIFO)
Queue<String> queue = new LinkedList<>();
queue.offer("A"); // Enqueue
String first = queue.poll(); // Dequeue

// 4. MAP (Dictionary)
Map<String, Integer> map = new HashMap<>();
map.put("Key", 10);

# Python Equivalent:
# 1. LINKED LIST
class Node:
    def __init__(self, data):
        self.data = data
        self.next = None

# 2. STACK (LIFO) - just use a list!
stack = []
stack.append(1) # Push
top = stack.pop() # Pop

# 3. QUEUE (FIFO) - use collections.deque
from collections import deque
queue = deque(["A"])
queue.append("B") # Enqueue
first = queue.popleft() # Dequeue

# 4. DICT
my_dict = {"Key": 10}`,
        qa: [
            {
                q: "When should I use LinkedList over ArrayList?",
                a: "Use **LinkedList** if your application requires frequent insertions or deletions at the beginning or middle of the list. Use **ArrayList** for almost all other cases, especially when you need fast random access (index-based lookups)."
            },
            {
                q: "What is the Java equivalent of a Python Dictionary?",
                a: "The **HashMap** is the most common equivalent. Both store data in key-value pairs and offer constant-time average performance for basic operations. For thread-safety, Java also offers **ConcurrentHashMap**."
            }
        ]
    },
    {
        id: 'jvm',
        title: 'JVM Internals',
        icon: '⚙️',
        bg: '#8b5cf6',
        tagline: 'Write Once, Run Anywhere',
        simple: 'The Java Virtual Machine (JVM) is the engine that drives Java applications. Understanding memory and GC is key to performance tuning.',
        keyPoints: [
            'Heap Memory: Stores objects, managed by Garbage Collector.',
            'Stack Memory: Stores primitive types and local variable references.',
            'Metaspace: Stores class metadata (replaces PermGen).',
            'Garbage Collection: Manual memory management is gone; GC (G1, ZGC) handles it.'
        ],
        code: `// Java: JVM Memory Arguments
// -Xms512m (Initial Heap)
// -Xmx2g   (Max Heap)
// -XX:+UseG1GC (Enable G1 Garbage Collector)

# Python Equivalent:
# Python handles memory automatically and globally.
# You don't usually set heap sizes via flags.
# gc module can be used to control Garbage Collection.
import gc
gc.disable() # Temporarily stop GC
gc.collect() # Manually trigger GC`,
        qa: [
            {
                q: "What is the difference between Stack and Heap memory?",
                a: "**Stack** is used for static memory allocation and thread execution (fast). **Heap** is used for dynamic memory allocation (longer-lived objects)."
            }
        ]
    },
    {
        id: 'concurrency',
        title: 'Concurrency & Threads',
        icon: '⚡',
        bg: '#ef4444',
        tagline: 'High Performance Execution',
        simple: 'Java provides a robust model for parallel execution, from raw Threads to the modern ExecutorService.',
        keyPoints: [
            'Thread: The smallest unit of execution.',
            'ExecutorService: Thread pool management for better resource usage.',
            'Synchronized / Lock: Preventing race conditions.',
            'CompletableFuture: Asynchronous, non-blocking programming.'
        ],
        code: `// Java: Asynchronous Programming
CompletableFuture.supplyAsync(() -> fetchUser())
    .thenApply(user -> processOrder(user))
    .thenAccept(result -> System.out.println("Done!"));

# Python Equivalent:
# Using asyncio for non-blocking execution
import asyncio

async def main():
    user = await fetch_user()
    result = await process_order(user)
    print("Done!")

asyncio.run(main())`,
        qa: [
            {
                q: "What is a Deadlock and how can we prevent it?",
                a: "A **Deadlock** happens when two threads are waiting for each other to release locks. Prevent it by obtaining locks in a fixed order or using timeouts."
            }
        ]
    },
    {
        id: 'masterclass',
        title: 'Java Masterclass',
        icon: '🎓',
        bg: '#10b981',
        tagline: 'Holistic View & System Integration',
        simple: 'Real-world software development requires combining OOP, memory management, and efficient data structures into a unified system. This Masterclass demonstrates how these pieces fit together to build scalable, high-precision applications.',
        keyPoints: [
            'Unified OOP: Combining the four pillars into a single "Workshop" model.',
            'Memory Insight: Visualizing Stack vs Heap through object lifecycle.',
            'Data Precision: Using BigDecimal for critical values within complex systems.',
            'Modern Orchestration: Using Streams and Lambdas to process data efficiently.',
            'Maintainable Contracts: Using Interfaces and Abstract classes to enforce standards.'
        ],
        code: `// Java: Holistic Masterclass Example
public class JavaMasterclass {
    public static void main(String[] args) {
        // 1. Memory: 'id' on Stack, 'tool' on Heap
        int id = 101;
        WorkshopTool tool = new Saw("Table Saw", ToolType.POWER);

        // 2. OOP: Polymorphism & Abstraction
        tool.use(); // "Sawing materials..."
        
        // 3. Modern Java: Streams processing
        List<String> items = Arrays.asList("Wood", "Glass");
        items.stream().filter(i -> i.length() > 4).forEach(System.out::println);
    }
}

# The Power of Java:
# - Type Safety (Compile-time checks)
# - Robust Memory Management (GC + Stack/Heap)
# - Expressive Abstractions (OOP & Functional)`,
        qa: [
            {
                q: "How does an object move from the Stack to the Heap in this Masterclass example?",
                a: "The **reference variable** ('tool') lives on the **Stack** as a pointer. The actual **object** ('new Saw(...)') is allocated on the **Heap**. When the method returns, the Stack reference is cleared, but the Heap object remains until the **Garbage Collector** identifies it as unreachable."
            },
            {
                q: "Why is it important to use Interfaces even in small projects like the Saw/Hammer demo?",
                a: "Interfaces define **contracts**. By using the 'Maintainable' interface, we can add new tools or systems without breaking existing coordination code. This is the essence of **Decoupling** and the **Open/Closed Principle**."
            }
        ]
    }
];
